/**
 * <made-in-badge>
 * The "<flag> Made in <location>" half of the product vendor line (see
 * blocks/product-vendor.liquid).
 *
 * Normally the production centre depends only on the shopper's selected country,
 * so the badge is plain server-rendered markup and this component never loads.
 *
 * It loads for the one exception: for the two European countries we ship to that
 * are outside the EU (Switzerland, Norway), the larger pieces are made at Prodigi's
 * GB lab rather than the NL one. That makes the badge depend on the Size and Frame
 * the shopper has picked, and a variant change only morphs the variant picker
 * itself — the vendor line is left untouched — so the swap has to be re-applied
 * here.
 *
 * WHICH sizes is decided entirely server-side, in blocks/product-vendor.liquid,
 * from the routing table in docs/wall-art-margins.md. This file receives the answer
 * as two ready-made lists of storefront size strings — the sizes that are GB when
 * framed, and when unframed — and does nothing but pick a list and test membership.
 * Two lists rather than one because the answer really does differ: framing moves a
 * paper print from the NL lab to the GB one, while on a canvas it changes nothing.
 * Keep it that way; this file should never learn what a sheet or a lab is.
 *
 * Both wordings are rendered server-side into data attributes (already translated
 * and already correct for the initially selected variant), so this only ever
 * swaps between two known strings; it never composes copy of its own.
 */
(() => {
  'use strict';

  /** Size option values are written "24" × 32"" — tolerate stray spacing around the ×. */
  const normalize = (value) => (value || '').trim().replace(/\s*×\s*/g, ' × ');

  class MadeInBadge extends HTMLElement {
    connectedCallback() {
      const { altSizesFramed, altSizesUnframed, altText, altFlag, baseText, baseFlag } = this.dataset;
      if (!altText || !baseText) return;

      try {
        this.altFramed = new Set(JSON.parse(altSizesFramed || '[]').map(normalize));
        this.altUnframed = new Set(JSON.parse(altSizesUnframed || '[]').map(normalize));
      } catch {
        return;
      }
      if (!this.altFramed.size && !this.altUnframed.size) return;

      this.unframedValue = normalize(this.dataset.unframedValue);
      this.text = { base: baseText, alt: altText };
      this.flag = { base: baseFlag, alt: altFlag };

      this.flagEl = this.querySelector('.product-vendor__flag');
      this.textEl = this.querySelector('.product-vendor__made-in');
      this.picker = (this.closest('.shopify-section') || document).querySelector('variant-picker');
      if (!this.textEl || !this.picker) return;

      this._apply = this._apply.bind(this);
      this._onMutation = this._onMutation.bind(this);

      // Bubble phase, so frame-size-fallback's capture-phase correction (which can
      // silently move the frame back to "Print Only") has already been applied.
      this.picker.addEventListener('change', this._apply);
      this._observer = new MutationObserver(this._onMutation);
      this._observer.observe(this.picker, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['data-current-checked', 'checked', 'selected', 'value'],
      });
    }

    disconnectedCallback() {
      this.picker?.removeEventListener('change', this._apply);
      this._observer?.disconnect();
      if (this._frame) cancelAnimationFrame(this._frame);
    }

    /** The value currently checked for a named option, buttons or dropdown. */
    _selected(optionName) {
      const selector = `input[type="radio"][data-option-name="${optionName}"]:checked, option[data-option-name="${optionName}"]:checked`;
      return normalize(this.picker.querySelector(selector)?.value);
    }

    /** A picker morph re-renders the inputs; re-read the selection once per frame. */
    _onMutation() {
      if (this._frame) cancelAnimationFrame(this._frame);
      this._frame = requestAnimationFrame(() => {
        this._frame = null;
        this._apply();
      });
    }

    _apply() {
      const frame = this._selected('Frame');
      const framed = frame !== '' && frame !== this.unframedValue;
      const gbSizes = framed ? this.altFramed : this.altUnframed;
      const key = gbSizes.has(this._selected('Size')) ? 'alt' : 'base';

      if (this.textEl.textContent !== this.text[key]) this.textEl.textContent = this.text[key];
      if (this.flagEl && this.flag[key] && this.flagEl.getAttribute('src') !== this.flag[key]) {
        this.flagEl.setAttribute('src', this.flag[key]);
      }
    }
  }

  if (!customElements.get('made-in-badge')) customElements.define('made-in-badge', MadeInBadge);
})();
